import {Component} from '@angular/core';
import {Role} from '../../model/auth/Role';
import {NotificationHandler} from '../../helper/notification.handler';
import {MobileAwareComponent} from '../../common/mobile-aware/mobile-aware.component';
import {BreakpointObserver} from '@angular/cdk/layout';
import {MatDialog} from '@angular/material/dialog';
import {ContestService} from '../../service/contest.service';
import {AvailableContestTasks, Contest} from '../../model/contest/Contest';
import {ActivatedRoute} from '@angular/router';
import {
  ContestTask,
  ContestTaskDto,
  ContestTaskType,
  localizeContestTaskType,
  Question,
  Section
} from '../../model/contest/ContestTask';
import {LocalStorageService} from 'ngx-webstorage';
import {LocalStorageKeys} from '../../helper/local-storage.keys';
import {ContestTaskService} from '../../service/contest.task.service';
import {Observable, of} from 'rxjs';
import {Locale, localizeLocale} from '../../model/Locale';
import {MultiActionItem} from '../../common/button/button.component';
import {
  DeleteConfirmationData,
  DeleteConfirmationModalComponent,
  DeleteContext
} from '../../common/modal/delete-confirmation-modal/delete-confirmation-modal.component';
import {localizeSchoolGrade, SchoolGrade} from '../../model/location/School';
import {MathService} from '../../math/math.service';
import {EventCategory, EventType, TrackingService} from '../../service/tracking.service';

@Component({
  selector: 'app-contests-tasks-editor-component',
  templateUrl: './contest-tasks-editor.component.html',
  styleUrls: ['./contest-tasks-editor.component.scss']
})
export class ContestTasksEditorComponent extends MobileAwareComponent {

  readonly roles = [Role.Host];

  contest?: Contest | null;
  availableTasks?: AvailableContestTasks;

  inProgress = false;
  changesMade = false;
  editMode = false;
  localizeMode = false;

  taskGrade?: SchoolGrade;
  taskType?: ContestTaskType;
  taskLocale?: Locale;

  tasks = new Map<SchoolGrade, Map<ContestTaskType, ContestTask | undefined>>();
  origTask?: ContestTask;

  ctaActions?: MultiActionItem[];

  constructor(private contestService: ContestService,
              private contestTaskService: ContestTaskService,
              private localStorageService: LocalStorageService,
              private trackingService: TrackingService,
              private mathService: MathService,
              private dialog: MatDialog,
              private route: ActivatedRoute,
              breakpointObserver: BreakpointObserver) {
    super(breakpointObserver);
  }

  initContent() {
    this.route.params.subscribe(params => {
      if (params && params.contestId) {
        this.loadContest(params.contestId as string);
      }
    });

    this.ctaActions = [{
      label: $localize`:General|Edit Translation action label@@general-edit-translation-action-label:Edit Translation`,
      action: 'edit-translation',
      icon: 'translate'
    }, {
      label: $localize`:General|Delete Translation action label@@general-delete-translation-action-label:Delete Translation`,
      action: 'delete-translation',
      icon: 'delete'
    }];
  }

  compareTaskGrades(g1?: SchoolGrade, g2?: SchoolGrade): boolean {
    return g1 == g2;
  }

  loadTaskGrades = (): Observable<SchoolGrade[]> => {
    return of([
      SchoolGrade.second, SchoolGrade.third, SchoolGrade.forth, SchoolGrade.fifth, SchoolGrade.sixth,
      SchoolGrade.seventh, SchoolGrade.eighth, SchoolGrade.ninth, SchoolGrade.tenth, SchoolGrade.eleventh
    ]);
  };

  displayTaskGrade(grade: SchoolGrade): string {
    return localizeSchoolGrade(grade);
  }

  compareTaskTypes(t1?: ContestTaskType, t2?: ContestTaskType): boolean {
    return t1 == t2;
  }

  loadTaskTypes = (): Observable<ContestTaskType[]> => {
    return of([
      ContestTaskType.standard,
      ContestTaskType.advanced
    ]);
  };

  displayTaskType(type: ContestTaskType): string {
    return localizeContestTaskType(type);
  }

  compareTaskLocales(l1?: Locale, l2?: Locale) {
    return l1 == l2;
  }

  loadTaskLocales = (): Observable<Locale[]> => {
    return of([Locale.uk, Locale.en, Locale.ru]);
  };

  displayTaskLocale(locale: Locale) {
    return localizeLocale(locale);
  }

  ctaActionSelected(action: string) {
    if (this.taskGrade && this.taskType && this.taskLocale) {
      switch (action) {
        case 'edit-translation':
          this.localizeTask(this.taskGrade, this.taskType, this.taskLocale);
          break;
        case 'delete-translation':
          this.deleteTaskLocalization(this.taskGrade, this.taskType, this.taskLocale);
          break;
      }
    }
  }

  private loadContest(id: string) {
    this.inProgress = true;
    this.contest = null;
    this.contestService.getDetails(id).subscribe(c => {
      this.availableTasks = c.tasks;
      this.contest = c;

      if (!this.checkTaskCanBeLoaded()) this.inProgress = false;
    }, error => {
      NotificationHandler.handleError($localize`:Notifications|Contest details fetch has failed message@@notifications-contest-details-fetch-failed-error:Contest details fetch has failed`, error);
      this.inProgress = false;
    });
  }

  get task(): ContestTask | undefined {
    let task: ContestTask | undefined = undefined;

    if (this.taskGrade && this.taskType && this.tasks.has(this.taskGrade)) {
      const gradeData = this.tasks.get(this.taskGrade);
      if (gradeData && gradeData.has(this.taskType)) task = gradeData.get(this.taskType);
    }

    return task;
  }

  private cacheTask(grade: SchoolGrade, type: ContestTaskType, task: ContestTask | undefined) {
    if (!this.tasks.has(grade)) this.tasks.set(grade, new Map<ContestTaskType, ContestTask | undefined>());
    const gradeData = this.tasks.get(grade);
    if (gradeData) gradeData.set(type, task);
  }

  private loadContestTask(contestId: string, taskId: string, grade: SchoolGrade, type: ContestTaskType) {
    this.inProgress = true;

    if (this.tasks.has(grade)) {
      const gradeData = this.tasks.get(grade);
      if (gradeData && gradeData.has(type)) {
        this.inProgress = false;

        this.renderMath();
        return;
      }
    }

    this.inProgress = true;
    this.contestTaskService.getContestTask(contestId, taskId, type).subscribe(task => {
      this.cacheTask(grade, type, task);

      this.inProgress = false;

      this.renderMath();
    }, error => {
      NotificationHandler.handleError($localize`:Notifications|Contest task details fetch has failed message@@notifications-contest-task-details-fetch-failed-error:Contest task details fetch has failed`, error);
      this.inProgress = false;
    });
  }

  private renderMath(): void {
    setTimeout(() => {
      this.mathService.renderPageMath();
    }, 1);
  }

  get contestEditAllowed() {
    return this.contest && this.contest.startsAt && this.contest.startsAt > new Date();
  }

  private static draftKey(contestId: string, type: ContestTaskType): string {
    return `${LocalStorageKeys.CONTEST_TASK_DRAFT}|${contestId}|${type}`;
  }

  private restoreLocalState(contestId: string, type: ContestTaskType): ContestTask | null {
    const key = ContestTasksEditorComponent.draftKey(contestId, type);
    const data = this.localStorageService.retrieve(key) as string;

    const draft = data
      ? ContestTask.parse(JSON.parse(data) as ContestTaskDto)
      : null;

    if (draft) {
      NotificationHandler.notification($localize`:Notifications|Contest task draft restored message@@notifications-contest-task-draft-restored-message:Contest task draft restored`);
    }

    return draft;
  }

  private saveDraft(): void {
    if (!this.taskGrade || !this.taskType || !this.taskLocale) return;

    const task = this.task;

    this.orderSections();

    if (task && this.contest) {
      const key = ContestTasksEditorComponent.draftKey(this.contest.id, this.taskType);
      this.localStorageService.store(key, JSON.stringify(task));
    }

    this.changesMade = true;
  }

  private clearDraft() {
    if (this.contest && this.taskType) {
      const key = ContestTasksEditorComponent.draftKey(this.contest.id, this.taskType);
      this.localStorageService.clear(key);
    }
  }

  addSection() {
    if (!this.taskGrade || !this.taskType || !this.taskLocale) return;

    const task = this.task;
    if (task) {
      task.sections[this.taskLocale].push(new Section());
      this.orderSections();
      this.saveDraft();
    }
  }

  private orderSections() {
    if (!this.taskGrade || !this.taskType || !this.taskLocale) return;

    const task = this.task;
    if (task) task.sections[this.taskLocale].forEach((section, index) => section.index = index + 1);
  }

  handleSectionUpdate(section: Section) {
    this.orderSections();
    this.renderMath();

    this.saveDraft();
  }

  deleteSection(section: Section) {
    if (!this.taskGrade || !this.taskType || !this.taskLocale) return;

    const task = this.task;
    if (!task) return;

    const index = task.sections[this.taskLocale].indexOf(section);
    if (index >= 0) {
      task.sections[this.taskLocale].splice(index, 1);

      this.handleSectionUpdate(section);
    }
  }

  get canProceedWithSave(): boolean {
    const task = this.task;

    return this.taskLocale != undefined
      && task != undefined
      && task.sections
      && task.sections[this.taskLocale].length > 0
      && task.sections[this.taskLocale].map(s => s.questions && s.questions.length > 0).reduce((s1, s2) => s1 && s2);
  }

  get taskTypeAvailable(): boolean {
    return this.taskGrade != undefined
      && this.taskType != undefined
      && this.availableTasks != undefined
      && this.availableTasks[this.taskGrade] != undefined
      && this.availableTasks[this.taskGrade][this.taskType] != undefined;
  }

  get taskLocaleAvailable(): boolean {
    return this.taskGrade != undefined
      && this.taskType != undefined
      && this.taskLocale != undefined
      && this.availableTasks != undefined
      && this.availableTasks[this.taskGrade] != undefined
      && this.availableTasks[this.taskGrade][this.taskType] != undefined
      && this.availableTasks[this.taskGrade][this.taskType][this.taskLocale] != undefined;
  }

  changeTaskGrade(grade: SchoolGrade) {
    this.taskGrade = grade;
    this.checkTaskCanBeLoaded();
  }

  changeTaskType(type: ContestTaskType) {
    this.taskType = type;
    this.checkTaskCanBeLoaded();
  }

  changeTaskLocale(locale: Locale) {
    this.taskLocale = locale;
    this.checkTaskCanBeLoaded();
  }

  private checkTaskCanBeLoaded(): boolean {
    if (this.contest
      && this.taskType
      && this.taskGrade
      && this.availableTasks
      && this.availableTasks[this.taskGrade]
      && this.availableTasks[this.taskGrade][this.taskType]) {
      const availableType = this.availableTasks[this.taskGrade][this.taskType];
      const ids = Object.keys(availableType)
        .map(locale => availableType[locale])
        .filter(id => id != undefined);

      if (ids.length > 0) {
        this.loadContestTask(this.contest.id, ids[0], this.taskGrade, this.taskType);
        return true;
      } else {
        return false;
      }
    } else {
      return false;
    }
  }

  createTask(grade: SchoolGrade, type: ContestTaskType, locale: Locale) {
    this.editMode = true;
    this.origTask = undefined;

    const task = new ContestTask(type);
    task.locales = [locale];
    task.sections[locale] = [];

    this.cacheTask(grade, type, task);

    this.addSection();
  }

  editTask(grade: SchoolGrade, type: ContestTaskType, locale: Locale) {
    const task = this.task;
    if (!task) return;

    this.origTask = task ? task.clone(true) : task;

    if (this.contest) {
      const draft = this.restoreLocalState(this.contest.id, type);
      if (draft) {
        this.cacheTask(grade, type, draft);
        this.renderMath();
      }
    }

    if (task && !task.sections[locale].length) this.addSection();

    this.editMode = true;
  }

  localizeTask(grade: SchoolGrade, type: ContestTaskType, locale: Locale) {
    let task = this.task;

    if (!task) return;  // we cannot localize before task is created

    this.origTask = task.clone(true);

    if (this.contest) {
      const draft = this.restoreLocalState(this.contest.id, type);
      if (draft) {
        this.cacheTask(grade, type, draft);
        task = draft;

        this.renderMath();
      }
    }

    if (!task.locales.includes(locale)) {
      task.locales.push(locale);
      const otherLocales = Object.keys(task.sections).filter(key => key != locale);

      if (otherLocales.length > 0) {
        if (!task.sections[locale]) task.sections[locale] = [];

        for (const section of task.sections[otherLocales[0]]) {
          const sections = task.sections[locale];
          if (sections.findIndex(s => s.id == section.id) < 0) {
            sections.push(section.clone(true));
          }
        }
      }
    }

    this.localizeMode = true;
  }

  deleteTaskLocalization(grade: SchoolGrade, type: ContestTaskType, locale: Locale) {
    const task = this.task;
    if (!task || !this.contest) return;

    const data: DeleteConfirmationData = {
      title: $localize`:General|Delete Translation action label@@general-delete-translation-action-label:Delete Translation`,
      itemType: $localize`:Contest Tasks Editor|Contest task translation delete item type@@contest-tasks-editor-task-translation-delete-item-type:Translation`,
      name: localizeLocale(locale),
      actionDesc: $localize`:Contest Tasks Editor|Contest task translation delete item prompt@@contest-tasks-editor-task-translation-delete-item-prompt:will be deleted and will not be available for others`,
      archive: false
    };
    const dialogRef = this.dialog.open(DeleteConfirmationModalComponent, {
      width: '600px',
      data: data
    });

    dialogRef.componentInstance.deleteConfirmed.subscribe((context: DeleteContext) => {
      const localeIndex = task.locales.indexOf(locale);
      if (localeIndex >= 0) {
        task.locales.splice(localeIndex, 1);
        if (task.sections[locale]) {
          delete task.sections[locale];

          this.inProgress = true;
          if (this.contest) this.contestTaskService.deleteContestTaskLocale(this.contest.id, grade, type, locale).subscribe(() => {
            context.modal.deleteHandled();

            if (this.availableTasks && this.availableTasks[grade]) {
              if (task.locales.length > 0) {
                this.cacheTask(grade, type, task);

                const availableTask = this.availableTasks[grade][type];
                if (availableTask) delete availableTask[locale];
              } else {
                const gradeData = this.tasks.get(grade);
                if (gradeData) gradeData.delete(type);
                delete this.availableTasks[grade][type];
              }
            }

            this.trackingService.sendEvent(EventType.contest_localization_removed, EventCategory.contest, this.contest?.id);

            this.inProgress = false;
          }, error => {
            NotificationHandler.handleError($localize`:Notifications|Contest task translation delete failed message@@notifications-contest-task-translation-delete-failed-error:Contest task translation delete failed`, error);
            this.inProgress = false;
          });
        }
      }
    });
  }

  cancelEdit() {
    if (!this.taskGrade || !this.taskType || !this.taskLocale) return;

    this.cacheTask(this.taskGrade, this.taskType, this.origTask);

    this.clearDraft();

    this.renderMath();

    this.changesMade = false;
    this.editMode = false;
    this.localizeMode = false;
  }

  saveTask() {
    if (!this.taskGrade || !this.taskType || !this.taskLocale) return;

    const task = this.task;
    if (!task) return;

    this.orderSections();

    if ((this.editMode || this.localizeMode) && this.contest) {
      this.inProgress = true;

      if (task.locales.length > 1) {
        const updatedSections = task.sections[this.taskLocale];

        const newSectionIds = new Set<string>();
        const newQuestionIds = new Set<string>();
        const newAnswersIds = new Set<string>();

        updatedSections.forEach(section => {
          if (section.id) newSectionIds.add(section.id);
          section.questions.forEach(question => {
            if (question.id) newQuestionIds.add(question.id);
            question.answers.forEach(answer => {
              if (answer.id) newAnswersIds.add(answer.id);
            });
          });
        });

        const otherLocates = Object.keys(task.sections)
          .filter(locale => locale != this.taskLocale);

        for (const locale of otherLocates) {
          const sectionIds = new Set<string>();
          const questionIds = new Set<string>();
          const answersIds = new Set<string>();

          const sections = task.sections[locale];
          const sectionsToDelete = sections.filter(section => section.id && !newSectionIds.has(section.id));
          sectionsToDelete.forEach(section => sections.splice(sections.indexOf(section), 1));

          sections.forEach(section => {
            if (section.id) sectionIds.add(section.id);

            const questions = section.questions;
            const questionsToDelete = questions.filter(question => question.id && !newQuestionIds.has(question.id));
            questionsToDelete.forEach(question => questions.splice(questions.indexOf(question), 1));

            questions.forEach(question => {
              if (question.id) questionIds.add(question.id);
              const answers = question.answers;

              const answersToDelete = answers.filter(answer => answer.id && !newAnswersIds.has(answer.id));
              answersToDelete.forEach(answer => answers.splice(answers.indexOf(answer), 1));

              answers.forEach(answer => {
                if (answer.id) answersIds.add(answer.id);
              });
            });
          });

          updatedSections.forEach(section => {
            let oldSection: Section | undefined = undefined;
            if (section.id) {
              if (!sectionIds.has(section.id)) {
                oldSection = section.clone(true);
                task.sections[locale].push(oldSection);
                sectionIds.add(section.id);
                oldSection.questions.forEach(question => {
                  if (question.id) questionIds.add(question.id);
                  question.answers.forEach(answer => {
                    if (answer.id) answersIds.add(answer.id);
                  });
                });
              } else {
                oldSection = task.sections[locale].find(s => s.id == section.id);
                if (oldSection) oldSection.index = section.index;
              }
            }

            section.questions.forEach(question => {
              let oldQuestion: Question | undefined = undefined;
              if (oldSection && question.id) {
                if (!questionIds.has(question.id)) {
                  oldQuestion = question.clone(true);
                  oldSection.questions.push(oldQuestion);
                  questionIds.add(question.id);
                  oldQuestion.answers.forEach(answer => {
                    if (answer.id) answersIds.add(answer.id);
                  });
                } else {
                  oldQuestion = oldSection.questions.find(q => q.id == question.id);
                  if (oldQuestion) {
                    oldQuestion.type = question.type;
                    oldQuestion.points = question.points;
                    oldQuestion.index = question.index;
                  }
                }
              }

              question.answers.forEach(answer => {
                if (oldQuestion && answer.id) {
                  if (!answersIds.has(answer.id)) {
                    oldQuestion.answers.push(answer.clone(true));
                    answersIds.add(answer.id);
                  } else {
                    const oldAnswer = oldQuestion.answers.find(a => a.id == answer.id);
                    if (oldAnswer) {
                      oldAnswer.correct = answer.correct;
                      oldAnswer.index = answer.index;
                    }
                  }
                }
              });
            });
          });
        }
      }

      task.prepareForSave();

      this.contestTaskService.saveContestTask(this.contest.id, task, this.taskGrade, this.taskType).subscribe(contestTask => {
        if (this.taskGrade && this.taskType && this.taskLocale) {
          this.cacheTask(this.taskGrade, this.taskType, contestTask);

          if (!this.availableTasks) this.availableTasks = {};
          if (!this.availableTasks[this.taskGrade]) this.availableTasks[this.taskGrade] = {};
          if (!this.availableTasks[this.taskGrade][this.taskType]) this.availableTasks[this.taskGrade][this.taskType] = {};
          this.availableTasks[this.taskGrade][this.taskType][this.taskLocale] = contestTask.id || '';
        }

        this.editMode = false;
        this.localizeMode = false;
        this.inProgress = false;

        this.clearDraft();

        this.renderMath();

        this.trackingService.sendEvent(EventType.contest_task_saved, EventCategory.contest, this.contest?.id);

        NotificationHandler.notification($localize`:Notifications|Contest task save successful message@@notifications-contest-task-save-successful-error:Contest task successfully saved`);
      }, error => {
        NotificationHandler.handleError($localize`:Notifications|Contest task save failed message@@notifications-contest-task-save-failed-error:Contest task save has failed`, error);
        this.inProgress = false;
      });
    }
  }
}
